/**
 * Deal counterparty model (Phase 2 — broker / proxy / synthetic deals).
 *
 * A Deal is an ÖPM-intermediated transaction between two **sides** — supply
 * (seller/offering) and demand (buyer/request). Each side is bound to a
 * {@link Party} value object, not to a fixed offering/request. Today both sides
 * are `real`; this model lets either side be non-real while the system still
 * records ÖPM's intent for that side. Commercial terms live on the Deal, never
 * on the Party — the Party is provenance + intent only.
 *
 * See `~/.claude/plans/match-repository-and-admin-deals-architecture.md`.
 */

/** The commercial side of a deal. Supply = seller/offering; demand = buyer/request. */
export type DealSide = "supply" | "demand";

export const DEAL_SIDES: readonly DealSide[] = ["supply", "demand"] as const;

/**
 * What kind of counterparty backs one side of a deal.
 * - `real` — a live ÖPM entity from a real user (today's deal). Terms come from
 *   the Deal, not the entity.
 * - `proxy_real` — a real counterparty we have in mind but whose terms we
 *   override (e.g. a price phone-agreed for this one case). The ref records
 *   *who*; the params are ours.
 * - `proxy_prospective` — a real user who *might* produce this supply/demand:
 *   a candidate, uncommitted, with no entity yet.
 * - `landvest_analysis` — develop via LandVest; a LandVest analysis backs
 *   feasibility. Referenced by URI, never an FK (bounded-context decoupling).
 * - `synthetic` — fully ÖPM-set; develop ourselves / placeholder. No
 *   counterparty entity, so no ref.
 */
export type CounterpartyKind =
  | "real"
  | "proxy_real"
  | "proxy_prospective"
  | "landvest_analysis"
  | "synthetic";

/** Single source for the kind vocabulary — the Zod enum (deals/types.ts) and the
 *  admin route derive from this; the SQL CHECK mirrors it. */
export const COUNTERPARTY_KINDS = [
  "real",
  "proxy_real",
  "proxy_prospective",
  "landvest_analysis",
  "synthetic",
] as const satisfies readonly CounterpartyKind[];

/** True for the one kind that carries no counterparty ref. */
export function isSyntheticKind(kind: CounterpartyKind): boolean {
  return kind === "synthetic";
}

/**
 * Short German label for a counterparty kind — used to render a NON-REAL side
 * (no user / no live entity) in admin notifications (Phase 2F). A `real` side
 * displays its actual user/entity instead, so it maps to a generic fallback.
 * Accepts `string` since the value often arrives off a DB column.
 */
export function counterpartyKindLabel(kind: CounterpartyKind | string): string {
  switch (kind) {
    case "proxy_real":
      return "Gegenpartei (modifiziert)";
    case "proxy_prospective":
      return "Gegenpartei (Produktion)";
    case "landvest_analysis":
      return "LandVest Kunde";
    case "synthetic":
      return "Custom";
    default:
      return "Gegenpartei";
  }
}

/**
 * One side of a deal — a value object.
 *
 * `ref` is an entity-pointer URI (ARCH-029, `@oekopunktemarkt/entity-pointers`)
 * identifying the counterparty resource — e.g.
 * `https://oekopunktemarkt.de/api/offerings/<uuid>` or
 * `https://landvest.eu/api/analyse/<uuid>`. It is treated as a REST resource
 * locator (the URI *is* the GET-details endpoint), resolved through the
 * counterparty resolver, never a SQL join. `ref` is `null` iff the kind is
 * `synthetic`.
 *
 * `intent` is a free-text note capturing ÖPM's plan for this side (e.g.
 * "phone-agreed 4.20 €/pt with Müller", "develop 30k pts via LandVest").
 */
export interface Party {
  role: DealSide;
  kind: CounterpartyKind;
  /** Entity-pointer URI, or `null` iff `kind === "synthetic"`. */
  ref: string | null;
  intent: string | null;
}
